import styled, { createGlobalStyle, keyframes } from 'styled-components'

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(6px); }
  to   { opacity: 1; transform: translateY(0); }
`

const c = {
  bg: '#0a0c0f', bg2: '#111318', bg3: '#181c23', bg4: '#1e2330',
  bdr: '#1f2430', bdr2: '#2a3040',
  acc: '#00e5a0', acc2: '#00b87a',
  adim: 'rgba(0,229,160,.08)', adim2: 'rgba(0,229,160,.18)',
  txt: '#e8ecf0', txt2: '#8a95a3', txt3: '#5a6370',
  warn: '#f59e0b', danger: '#ef4444', info: '#3b82f6',
  mono: "'Space Mono',monospace", sans: "'DM Sans',sans-serif",
}

export const GlobalDevStyles = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
`

export const PageWrap = styled.div`
  background:${c.bg}; color:${c.txt}; font-family:${c.sans};
  min-height:100vh; display:flex; flex-direction:column;
`

export const Topbar = styled.div`
  background:${c.bg2}; border-bottom:1px solid ${c.bdr};
  padding:10px 16px; height:48px;
  display:flex; align-items:center; gap:14px; flex-shrink:0;
  h2 { font-size:13px; font-weight:600; color:${c.txt}; }
  .crumb { font-size:11px; color:${c.txt3}; }
  @media (max-width: 600px) { padding:10px 12px; .crumb { display:none; } }
`

export const Body = styled.div`
  flex:1; display:grid; grid-template-columns: 1fr 380px;
  min-height:0;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`

export const MainCol = styled.div`
  display:flex; flex-direction:column; gap:14px;
  padding:24px 28px; overflow-y:auto;
  @media (max-width: 600px) { padding:14px 12px; }
`

export const ScanCard = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:12px;
  padding:20px 22px;
  h3 { font-size:15px; font-weight:600; color:${c.txt}; margin-bottom:4px; }
  p  { font-size:12px; color:${c.txt3}; margin-bottom:14px; line-height:1.5; }
`

export const ScanInput = styled.input`
  width:100%; background:${c.bg3};
  border:2px solid ${({ $kind }) => $kind === 'err' ? c.danger : $kind === 'ok' ? c.acc : c.bdr2};
  border-radius:10px; padding:14px 16px;
  color:${c.txt}; font-size:16px; font-family:${c.mono};
  outline:none; text-align:center; letter-spacing:.04em;
  transition:.15s;
  &:focus { border-color:${c.acc}; box-shadow:0 0 0 4px ${c.adim}; }
  &::placeholder { color:${c.txt3}; }
  &:disabled { opacity:.6; }
`

export const HintRow = styled.div`
  display:flex; align-items:center; gap:8px; margin-top:10px;
  font-size:11px; color:${c.txt3};
  .count { color:${c.acc}; font-family:${c.mono}; font-weight:700; }
`

export const ListPanel = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-radius:12px;
  padding:14px 18px;
  display:flex; flex-direction:column; gap:10px;
  .header {
    display:flex; align-items:center; justify-content:space-between;
    h4 { font-size:12px; font-weight:600; color:${c.txt3};
         text-transform:uppercase; letter-spacing:.06em; }
    .actions { display:flex; gap:6px; }
  }
`

export const RastreiosList = styled.div`
  display:flex; flex-direction:column; gap:5px;
  max-height:340px; overflow-y:auto; padding-right:4px;
  &::-webkit-scrollbar { width:4px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
`

export const RastreioRow = styled.div`
  display:grid; grid-template-columns: 28px 1fr auto; gap:10px;
  align-items:center;
  background:${c.bg3}; border:1px solid ${c.bdr};
  border-left:3px solid ${c.acc2};
  border-radius:7px; padding:8px 12px;
  animation:${fadeIn} .12s ease;
  .idx { font-family:${c.mono}; font-size:10px; color:${c.txt3}; font-weight:700; text-align:center; }
  .rastreio { font-family:${c.mono}; font-size:12px; font-weight:700; color:${c.txt};
              white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .remove {
    background:transparent; border:1px solid ${c.bdr2}; color:${c.txt3};
    border-radius:5px; padding:3px 8px; font-size:11px; cursor:pointer;
    transition:.12s;
    &:hover { border-color:${c.danger}; color:${c.danger}; }
  }
`

export const BulkActionBtn = styled.button`
  width:100%; padding:14px;
  background:${c.danger}; border:none; color:#fff;
  border-radius:10px; font-size:14px; font-weight:700; font-family:${c.sans};
  cursor:pointer; transition:.15s;
  &:hover { filter:brightness(1.1); }
  &:disabled { opacity:.5; cursor:not-allowed; }
`

export const SmallBtn = styled.button`
  padding:5px 10px; border-radius:6px; cursor:pointer;
  font-size:11px; font-weight:600; font-family:${c.sans};
  background:transparent; border:1px solid ${c.bdr2}; color:${c.txt2};
  transition:.12s;
  &:hover { border-color:${c.danger}; color:${c.danger}; }
  &:disabled { opacity:.5; cursor:not-allowed; }
`

// ─── Side: resumo da sessão + ocorrências da última devolução ─────────────
export const SideCol = styled.aside`
  background:${c.bg2}; border-left:1px solid ${c.bdr};
  display:flex; flex-direction:column; overflow:hidden;
  @media (max-width: 900px) { border-left:none; border-top:1px solid ${c.bdr}; }
`
export const SideHeader = styled.div`
  padding:14px 18px; border-bottom:1px solid ${c.bdr};
  h3 { font-size:13px; font-weight:600; color:${c.txt}; margin-bottom:3px; }
  p { font-size:11px; color:${c.txt3}; }
`
export const SummaryStats = styled.div`
  padding:12px 18px; border-bottom:1px solid ${c.bdr};
  display:grid; grid-template-columns:repeat(3, 1fr); gap:8px;
  background:${c.bg3};
`
export const SummaryStat = styled.div`
  text-align:center;
  .l { font-size:9px; color:${c.txt3}; text-transform:uppercase; letter-spacing:.05em; }
  .v {
    font-family:${c.mono}; font-size:18px; font-weight:700;
    color:${({ $variant }) =>
      $variant === 'ok' ? c.acc :
      $variant === 'warn' ? c.warn :
      $variant === 'err' ? c.danger :
      c.txt};
  }
`
export const OccList = styled.div`
  flex:1; overflow-y:auto; padding:10px 14px;
  &::-webkit-scrollbar { width:4px; }
  &::-webkit-scrollbar-thumb { background:${c.bdr2}; border-radius:2px; }
  > h4 { font-size:11px; color:${c.txt3}; text-transform:uppercase;
         letter-spacing:.05em; margin-bottom:8px; font-weight:600; }
`
export const OccEmpty = styled.div`
  padding:20px 14px; text-align:center; color:${c.txt3}; font-size:11px; line-height:1.5;
`
export const OccItem = styled.div`
  background:${c.bg3}; border:1px solid ${c.bdr};
  border-left:3px solid ${({ $reason }) =>
    $reason === 'volume_embarcado' ? c.warn :
    $reason === 'order_has_embarcado_volume' ? c.warn :
    c.danger};
  border-radius:7px; padding:8px 12px; margin-bottom:6px;
  animation:${fadeIn} .15s ease;
  .rastreio { font-family:${c.mono}; font-size:11px; font-weight:700; color:${c.txt}; }
  .msg { font-size:10px; color:${c.txt2}; margin-top:3px; line-height:1.4; }
  .tag {
    display:inline-block; margin-top:4px;
    font-size:9px; font-weight:700; text-transform:uppercase; letter-spacing:.05em;
    padding:2px 7px; border-radius:6px;
    background:${({ $reason }) =>
      $reason === 'volume_embarcado' ? 'rgba(245,158,11,.18)' :
      $reason === 'order_has_embarcado_volume' ? 'rgba(245,158,11,.18)' :
      'rgba(239,68,68,.18)'};
    color:${({ $reason }) =>
      $reason === 'volume_embarcado' ? c.warn :
      $reason === 'order_has_embarcado_volume' ? c.warn :
      c.danger};
  }
`

// ─── Modal de confirmação ─────────────────────────────────────────────────
export const ConfirmOverlay = styled.div`
  position:fixed; inset:0; z-index:2000;
  background:rgba(0,0,0,.7); backdrop-filter:blur(3px);
  display:${({ $visible }) => $visible ? 'flex' : 'none'};
  align-items:center; justify-content:center; padding:20px;
`
export const ConfirmBox = styled.div`
  background:${c.bg2}; border:1px solid ${c.bdr2}; border-top:3px solid ${c.danger};
  border-radius:12px; padding:24px 26px;
  width:440px; max-width:calc(100vw - 20px);
  text-align:center;
  animation:${fadeIn} .15s ease;
  .icon { font-size:42px; margin-bottom:10px; }
  h3 { font-size:15px; font-weight:700; color:${c.txt}; margin-bottom:8px; }
  p { font-size:13px; color:${c.txt2}; margin-bottom:18px; line-height:1.5; }
  .actions {
    display:flex; gap:8px; justify-content:flex-end;
    @media (max-width: 600px) { flex-direction:column-reverse; }
  }
`
export const ConfirmBtn = styled.button`
  padding:10px 20px; border-radius:8px; cursor:pointer;
  font-size:13px; font-weight:700; font-family:${c.sans};
  background:${({ $danger }) => $danger ? c.danger : 'transparent'};
  border:1px solid ${({ $danger }) => $danger ? c.danger : c.bdr2};
  color:${({ $danger }) => $danger ? '#fff' : c.txt2};
  transition:.12s;
  &:hover { ${({ $danger }) => $danger ? 'filter:brightness(1.1);' : `border-color:${c.danger}; color:${c.danger};`} }
  &:disabled { opacity:.5; cursor:not-allowed; }
  @media (max-width: 600px) { width:100%; padding:13px; }
`
